// Library Methods
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { DownloadIcon, EditIcon, TrashIcon } from "src/assets/icons";
import { ACCENT_TEAL } from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";
import {
  Bullets,
  Callout,
  Chapter,
  GUIDE_NAMESPACE,
  GuideTable,
  Paragraph,
  Rich,
  SubHeading,
  WhoRow,
} from "../GuideText/GuideText";
import { MockStatusChip, MockUpdateChip } from "../GuideMocks/PortalMocks";
import {
  ManageWalkthrough,
  UninstallWalkthrough,
  UpdateWalkthrough,
} from "../Walkthroughs/CareWalkthroughs";

// Styles
import {
  getRequiredErrorColor,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

export const UpdateChapter = () => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);

  return (
    <Chapter
      id="update"
      eyebrow="Look after it"
      icon={<DownloadIcon width="16px" height="16px" />}
      title="Update the hub"
    >
      <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gap="6px"
        fontSize="14px"
      >
        {t("When a newer hub is available, the card shows an orange")}
        <MockUpdateChip version="2.5.0" />
        {t("label and the download icon turns orange.")}
      </Box>
      <UpdateWalkthrough />
      <Paragraph text='The hub keeps all its connections and anything waiting to be sent. If setup shows the token screen, leave it empty and click Next: "Leave the token empty to keep the enrollment it already has."' />
      <Typography fontSize="14px" color={mutedText}>
        {t(
          "Only people with access to the hub's home clinic can download it. Others see: \"Someone with access to the hub's home clinic can download it.\""
        )}
      </Typography>
    </Chapter>
  );
};

export const ManageChapter = () => {
  const theme = useTheme();

  return (
    <Chapter
      id="manage"
      eyebrow="Look after it"
      icon={<EditIcon width="16px" height="16px" />}
      title="Rename, share or delete a hub"
    >
      <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
      <ManageWalkthrough />
      <Bullets
        items={[
          "<b>Share with another of your clinics:</b> add it under <b>Associated Clinics</b>. The first clinic, the <b>Home Clinic</b>, always stays.",
        ]}
      />
      <Box component="ul" sx={{ margin: 0, paddingLeft: "22px" }}>
        <Typography component="li" fontSize="14px" lineHeight={1.6}>
          <Rich text="<b>Delete:</b> the hub stops for good and its card shows" />{" "}
          <Box
            component="span"
            display="inline-block"
            sx={{ verticalAlign: "middle" }}
          >
            <MockStatusChip
              text="Revoked"
              color={getRequiredErrorColor(theme)}
            />
          </Box>
          {". "}
          <Rich text="Then uninstall <b>RetinaLogik EMR Hub</b> from the PC (Windows Settings, Apps). Exams already received stay in RetinaLogik." />
        </Typography>
      </Box>
    </Chapter>
  );
};

const FAQ_ITEMS = [
  {
    question: "Does the hub PC have to stay on all the time?",
    answer:
      'Yes, during clinic hours at least. When it is off, nothing is collected. After 10 minutes without contact the portal shows "No contact since ...". When the PC comes back, the hub catches up on its own.',
  },
  {
    question: "What happens after a Windows restart or update?",
    answer:
      "The hub starts by itself. Nobody needs to sign in or open anything.",
  },
  {
    question: "Is there anything to open on the hub PC?",
    answer:
      "No. The hub has no window and no icon. Everything is done from the Data Hub page in the portal.",
  },
  {
    question: "The internet went down. Did we lose exams?",
    answer:
      "No. Exams wait in the hub's line (the <b>Queue</b> number on the card) and are sent when the connection is back. A file only leaves the line once RetinaLogik confirms it arrived.",
  },
  {
    question: "Why did an exam from the PAC server not come in?",
    answer:
      "The hub only brings exams for patients on the worklist. Check that the patient was booked, that the worklist is <b>Working</b>, and that the exam type is ticked under <b>Bring in their exams</b>.",
  },
  {
    question: "Will the archive fill up with old reports?",
    answer:
      "No. Only reports of exams from the date you chose under <b>Send reports of exams from</b> are sent.",
  },
  {
    question: "Could the hub pick up its own reports as new exams?",
    answer:
      "No. Reports the hub sends back are marked as RetinaLogik's own, and a report folder can never also be an exam folder.",
  },
  {
    question: "We are replacing the hub PC. What do we do?",
    answer:
      "Contact RetinaLogik support before you switch. They will help you move the hub without losing its connections.",
  },
  {
    question: "Our IT team wants to install it silently on many PCs.",
    answer:
      "Give IT the downloaded zip. They extract it and run <code>msiexec /i EmrHub.msi /qn</code> from that folder, with enrollment.json next to the installer. Each hub needs its own download.",
  },
];

const FaqItem = ({
  question,
  answer,
}: {
  question: string;
  answer: string;
}) => {
  const { t } = useTranslation(GUIDE_NAMESPACE);
  const theme = useTheme();
  const { surface, dividerBorder } = getSurfaceTokens(theme);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      sx={{
        backgroundColor: surface,
        border: dividerBorder,
        borderRadius: "8px",
      }}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          width: "100%",
          font: "inherit",
          color: "inherit",
          background: "none",
          border: 0,
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
          padding: "12px 14px",
          textAlign: "left",
          fontWeight: 700,
          fontSize: "14px",
          "&:focus-visible": { outline: "3px solid #35BCFD" },
        }}
      >
        {t(question)}
        <Box
          component="span"
          color={ACCENT_TEAL}
          fontSize="20px"
          lineHeight={1}
        >
          {isOpen ? "−" : "+"}
        </Box>
      </Box>
      {isOpen && (
        <Box padding="0 14px 12px">
          <Paragraph text={answer} />
        </Box>
      )}
    </Box>
  );
};

export const FaqChapter = () => (
  <Chapter id="faq" eyebrow="Look after it" title="Questions">
    <Box display="flex" flexDirection="column" gap="8px">
      {FAQ_ITEMS.map((item) => (
        <FaqItem
          key={item.question}
          question={item.question}
          answer={item.answer}
        />
      ))}
    </Box>
  </Chapter>
);

export const WordsChapter = () => (
  <Chapter id="words" eyebrow="Look after it" title="Words explained">
    <GuideTable
      columns={["Word", "What it means"]}
      rows={[
        [
          "<b>Hub / hub PC</b>",
          'The program, and the clinic computer it runs on, that moves exams between your clinic and RetinaLogik. In some screens it is called the "EMR Hub" or a "Data Agent".',
        ],
        ["<b>Worklist</b>", "The list of patients booked for the day."],
        [
          "<b>New / returning patient</b>",
          "A new patient was created in RetinaLogik from the worklist. A returning patient was already in RetinaLogik.",
        ],
        [
          "<b>PAC server</b> (PACS)",
          "The clinic's archive that stores exams from many machines, like FORUM or Harmony. A RIS is a similar scheduling system.",
        ],
        [
          "<b>DICOM</b>",
          "The common language eye-care machines and archives use to send exams to each other.",
        ],
        [
          "<b>AE title</b>",
          "The short name a machine or archive uses for itself in DICOM, like CIRRUS01 or EMRHUB. Both sides must use exactly the same spelling.",
        ],
        [
          "<b>Host / IP address</b>",
          "The address of a computer on your network, like 10.0.0.20.",
        ],
        [
          "<b>Port</b>",
          "A door number on that computer, like 104. The machine and the hub must agree on it.",
        ],
        [
          "<b>Shared folder</b>",
          "A folder other computers can open over the network. Its path starts with two backslashes, like <code>\\\\emr01\\worklist</code>.",
        ],
        ["<b>CSV</b>", "A simple table saved as text. Excel can open it."],
        ["<b>EMR</b>", "Your clinic's electronic medical record software."],
        [
          "<b>Enrollment</b>",
          "The one-time step, done by the installer, that links the hub PC to your clinic in RetinaLogik.",
        ],
      ]}
    />
  </Chapter>
);

export const UninstallOldChapter = () => (
  <Chapter
    id="uninstall-old"
    eyebrow="Addendum"
    icon={<TrashIcon width="16px" height="16px" />}
    title="Remove the old version"
  >
    <WhoRow
      items={[
        { kind: "you", text: "You can do this" },
        { kind: "it", text: "needs someone who can approve installs" },
      ]}
    />
    <Paragraph text="Some clinics still have the old program, called <b>RetinaLogik EMR</b>, on their PC. The new Data Hub replaces it, so the old one must be removed. Do this on every PC that has it, before you install the new hub." />
    <Paragraph text="Not sure? Follow the steps below. If Windows finds nothing, there is nothing to remove." />
    <UninstallWalkthrough />
    <Callout
      tone="warning"
      text="Remove only <b>RetinaLogik EMR</b>. Never remove <b>RetinaLogik EMR Hub</b>: that is the new Data Hub. If you remove it by mistake, the hub stops sending exams until it is installed again."
    />
    <SubHeading text="Other ways to get there" />
    <Bullets
      items={[
        "<b>From Settings:</b> click Start, open <b>Settings</b>, then <b>Apps</b>, then <b>Installed apps</b>.",
        "<b>On Windows 10:</b> the page is called <b>Apps & features</b>. Click the app's name, then <b>Uninstall</b>.",
      ]}
    />
    <SubHeading text="If it does not work" />
    <Bullets
      items={[
        "<b>The Uninstall button is greyed out, or Windows says you need permission:</b> ask someone who can approve installs on that PC.",
        "<b>Windows says the program is in use:</b> restart the PC and try again.",
        "<b>It still shows in the list afterwards:</b> close Settings, open it again and search once more. If it is still there, contact RetinaLogik support.",
      ]}
    />
  </Chapter>
);
